import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ConsumerTable from './Components/ConsumerTable'
import IdProofModel from './Components/IdProofModel'
import Loader from '@/components/Loader'
import TaskModal from '@/components/Modal'
import CustomPageNavigator from '@/components/Pagination'
import useLocalStorage from '@/hooks/useLocalStorage'
import { UserInterface } from '@/services/types/APIResponseType'
import useRegisteredConsumer from '@/services/hooks/useRegisteredConsumer.'
import Path from '@/router/localRoutes'
import './RegisteredConsumer.css'
import { styled, alpha } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import InputBase from '@mui/material/InputBase'
import DataUnavailable from '@/components/DataUnavailable'

const RegisteredConsumer = () => {
  const [open, setOpen] = useState<boolean>(false)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [currentId, setCurrentID] = useState<UserInterface>({} as UserInterface)
  const [isFlipped, setIsFlipped] = useState(true)
  const [searchValue, setSearchValue] = useState('')
  const [debouncedValue, setDebouncedValue] = useState(searchValue)
  const [, setConsumerId] = useLocalStorage('ConsumerId')

  const navigate = useNavigate()

  const Search = styled('div')(({ theme }) => ({
    display: 'flex',
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginLeft: 0,
    width: '50%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1),
      width: 'auto',
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  }))

  const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }))

  const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    width: '100%',
    '& .MuiInputBase-input': {
      padding: theme.spacing(1, 1, 1, 0),
      paddingLeft: `calc(1em + ${theme.spacing(4)})`,
      transition: theme.transitions.create('width'),
      [theme.breakpoints.up('sm')]: {
        width: '23ch',
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
  }))

  const itemsPerPage = 5
  const { data: registerConsumers, isLoading } = useRegisteredConsumer(
    currentPage,
    itemsPerPage,
    debouncedValue,
  )

  const handleOpen = (item: UserInterface) => {
    setCurrentID(item)
    setOpen(true)
  }

  const handleClose = () => {
    setIsFlipped(true)
    setOpen(false)
  }

  const handleViewDetails = (id: string) => {
    navigate(Path.RegisteredConsumerDetails)
    setConsumerId(id)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
  }

  const handleFlip = () => {
    setIsFlipped(!isFlipped)
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchValue)
    }, 400)

    return () => {
      clearTimeout(handler)
    }
  }, [searchValue])

  if (isLoading) {
    return <Loader />
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'end',
          marginRight: '20px',
          marginTop: '20px',
        }}
      >
        <div></div>
        <Search>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder="Search…"
            inputProps={{ 'aria-label': 'search' }}
            value={searchValue}
            onChange={handleSearchChange}
            autoFocus
          />
        </Search>
      </div>

      {registerConsumers &&
      registerConsumers?.data.consumerProfiles.length > 0 ? (
        <>
          <ConsumerTable
            consumersData={registerConsumers?.data.consumerProfiles}
            handleOpen={handleOpen}
            handleViewDetails={handleViewDetails}
          />
          <TaskModal
            open={open}
            content={
              <IdProofModel
                currentConsumer={currentId}
                isFlipped={isFlipped}
                isModelOpen={open}
                handleClickClose={handleClose}
                handleFlip={handleFlip}
              />
            }
            handleClose={handleClose}
          />
          <CustomPageNavigator
            currentPage={currentPage}
            totalItemCount={registerConsumers?.data.totalCount}
            limitPerPage={itemsPerPage}
            setCurrentPage={setCurrentPage}
          />
        </>
      ) : (
        <DataUnavailable description="No record found" />
      )}
    </div>
  )
}

export default RegisteredConsumer
